import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    X,
    Save,
    RotateCcw,
    Target,
    Layers,
    Image,
    FlaskConical,
    Users,
    RefreshCw,
    AlertCircle,
    GripVertical
} from 'lucide-react';
import { useFunnelBuilder } from '../../contexts/FunnelBuilderContext';
import FunnelSummaryPanel from './FunnelSummaryPanel';
import {
    CampaignForm,
    AdSetForm,
    AdForm,
    ABTestForm,
    SegmentForm,
    RetargetingPoolForm
} from './forms';
import ConfirmationModal from '../ConfirmationModal';

// Panel size constraints
const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_WIDTH = 600;
const DEFAULT_PANEL_WIDTH = 420;

/**
 * PropertiesPanel - Right-side panel for editing selected nodes
 * Shows FunnelSummaryPanel when no node is selected
 * Resizable via drag handle on left edge
 */
const PropertiesPanel = ({
    platformConnection,
    onConnectPlatform,
    onOpenCreativePicker,
    onGenerateCreative,
    brandContext
}) => {
    const {
        selectedNode,
        selectedNodeData,
        isFormDirty,
        updateSelectedNodeData,
        saveSelectedNode,
        discardSelectedNodeChanges,
        clearSelection,
        activePlatform,
        nodes
    } = useFunnelBuilder();

    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
    const [errors, setErrors] = useState({});

    // Panel resize state
    const [panelWidth, setPanelWidth] = useState(() => {
        // Try to restore from localStorage
        const saved = localStorage.getItem('propertiesPanelWidth');
        return saved ? Math.min(Math.max(parseInt(saved, 10), MIN_PANEL_WIDTH), MAX_PANEL_WIDTH) : DEFAULT_PANEL_WIDTH;
    });
    const [isResizing, setIsResizing] = useState(false);
    const resizeRef = useRef(null);

    // Handle resize drag
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing) return;

            // Calculate new width based on mouse position from right edge
            const newWidth = window.innerWidth - e.clientX;
            const clampedWidth = Math.min(Math.max(newWidth, MIN_PANEL_WIDTH), MAX_PANEL_WIDTH);
            setPanelWidth(clampedWidth);
        };

        const handleMouseUp = () => {
            if (isResizing) {
                setIsResizing(false);
                // Save to localStorage
                localStorage.setItem('propertiesPanelWidth', panelWidth.toString());
            }
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isResizing, panelWidth]);

    // Wrapper component for the resizable panel container
    const ResizableContainer = ({ children }) => (
        <div style={{
            width: `${panelWidth}px`,
            background: 'var(--color-surface)',
            borderLeft: '1px solid var(--color-border)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
            position: 'relative'
        }}>
            {/* Resize Handle */}
            <div
                ref={resizeRef}
                onMouseDown={handleMouseDown}
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '6px',
                    cursor: 'col-resize',
                    background: isResizing ? 'var(--color-primary)' : 'transparent',
                    transition: isResizing ? 'none' : 'background 0.15s',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                    if (!isResizing) e.currentTarget.style.background = 'var(--color-border)';
                }}
                onMouseLeave={(e) => {
                    if (!isResizing) e.currentTarget.style.background = 'transparent';
                }}
            >
                <GripVertical
                    size={12}
                    style={{
                        color: isResizing ? 'white' : 'var(--color-text-tertiary)',
                        opacity: isResizing ? 1 : 0,
                        transition: 'opacity 0.15s'
                    }}
                />
            </div>
            {children}
        </div>
    );

    // If no node is selected, show the summary panel
    if (!selectedNode || !selectedNodeData) {
        return (
            <ResizableContainer>
                <FunnelSummaryPanel />
            </ResizableContainer>
        );
    }

    const nodeType = selectedNodeData.type;

    // Validate form before saving
    const validate = () => {
        const newErrors = {};

        switch (nodeType) {
            case 'campaign':
                if (!selectedNodeData.name?.trim()) {
                    newErrors.name = 'Campaign name is required';
                }
                if (!selectedNodeData.budget?.amount || selectedNodeData.budget.amount <= 0) {
                    newErrors.budget = 'Budget must be greater than 0';
                }
                break;
            case 'adset':
                if (!selectedNodeData.name?.trim()) {
                    newErrors.name = 'Ad Set name is required';
                }
                break;
            case 'ad':
                if (!selectedNodeData.name?.trim()) {
                    newErrors.name = 'Ad name is required';
                }
                if (!selectedNodeData.headline?.trim()) {
                    newErrors.headline = 'Headline is required';
                }
                break;
            case 'abtest':
                if (!selectedNodeData.name?.trim()) {
                    newErrors.name = 'Test name is required';
                }
                break;
            case 'segment':
                if (!selectedNodeData.name?.trim()) {
                    newErrors.name = 'Segment name is required';
                }
                break;
            case 'retargetingPool':
                if (!selectedNodeData.name?.trim()) {
                    newErrors.name = 'Pool name is required';
                }
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (validate()) {
            saveSelectedNode();
        }
    };

    const handleDiscard = () => {
        if (isFormDirty) {
            setShowDiscardConfirm(true);
        } else {
            clearSelection();
        }
    };

    const confirmDiscard = () => {
        discardSelectedNodeChanges();
        setShowDiscardConfirm(false);
    };

    const handleClose = () => {
        if (isFormDirty) {
            setShowDiscardConfirm(true);
        } else {
            clearSelection();
        }
    };

    // Get parent campaign for context (used by AdForm)
    const getParentCampaign = () => {
        if (nodeType === 'ad' || nodeType === 'adset') {
            // Find parent campaign through edges
            const parentCampaign = nodes.find(n => n.type === 'campaign');
            return parentCampaign?.data;
        }
        return null;
    };

    const typeConfig = {
        campaign: {
            icon: Target,
            label: 'Campaign',
            color: 'var(--color-primary)'
        },
        adset: {
            icon: Layers,
            label: 'Ad Set',
            color: 'var(--color-info)'
        },
        ad: {
            icon: Image,
            label: 'Ad',
            color: 'var(--color-accent)'
        },
        abtest: {
            icon: FlaskConical,
            label: 'A/B Test',
            color: 'var(--color-warning)'
        },
        segment: {
            icon: Users,
            label: 'Segment',
            color: 'var(--color-success)'
        },
        retargetingPool: {
            icon: RefreshCw,
            label: 'Retargeting Pool',
            color: 'var(--color-warning)'
        }
    };

    const config = typeConfig[nodeType] || typeConfig.campaign;
    const Icon = config.icon;

    // Render the appropriate form based on node type
    const renderForm = () => {
        const commonProps = {
            formData: selectedNodeData,
            onChange: updateSelectedNodeData,
            errors
        };

        switch (nodeType) {
            case 'campaign':
                return <CampaignForm {...commonProps} showPlatformSelector={false} />;

            case 'adset':
                return (
                    <AdSetForm
                        {...commonProps}
                        platform={activePlatform}
                        platformConnection={platformConnection}
                        onConnectPlatform={onConnectPlatform}
                        campaignType={getParentCampaign()?.objective}
                        brandContext={brandContext}
                    />
                );

            case 'ad':
                return (
                    <AdForm
                        {...commonProps}
                        onOpenCreativePicker={onOpenCreativePicker}
                        onGenerateCreative={onGenerateCreative}
                        funnelContext={{
                            funnelStage: selectedNodeData.funnelStage || getParentCampaign()?.funnelStage
                        }}
                    />
                );

            case 'abtest':
                return <ABTestForm {...commonProps} variantCount={selectedNodeData.variantIds?.length || 2} />;

            case 'segment':
                return <SegmentForm {...commonProps} />;

            case 'retargetingPool':
                return <RetargetingPoolForm {...commonProps} />;

            default:
                return (
                    <div style={{
                        padding: 'var(--space-4)',
                        textAlign: 'center',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        <AlertCircle size={32} style={{ marginBottom: 'var(--space-2)' }} />
                        <div>Unknown node type: {nodeType}</div>
                    </div>
                );
        }
    };

    return (
        <ResizableContainer>
            {/* Header */}
            <div style={{
                padding: 'var(--space-3) var(--space-4)',
                borderBottom: '1px solid var(--color-border)',
                background: 'var(--color-bg-secondary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)'
                }}>
                    <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: 'var(--radius-md)',
                        background: `${config.color}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Icon size={18} style={{ color: config.color }} />
                    </div>
                    <div>
                        <div style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)'
                        }}>
                            Edit {config.label}
                        </div>
                        <div style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)',
                            maxWidth: '180px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {selectedNodeData.name || 'Untitled'}
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                    {/* Dirty indicator */}
                    {isFormDirty && (
                        <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: 'var(--color-warning)',
                            marginRight: 'var(--space-2)'
                        }} />
                    )}
                    <button
                        className="btn btn-icon btn-ghost"
                        onClick={handleClose}
                        title="Close panel"
                        style={{ padding: 'var(--space-2)' }}
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* Form Content - Scrollable */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: 'var(--space-4)'
            }}>
                {renderForm()}
            </div>

            {/* Footer with Actions */}
            <div style={{
                padding: 'var(--space-3) var(--space-4)',
                borderTop: '1px solid var(--color-border)',
                background: 'var(--color-bg-secondary)',
                display: 'flex',
                gap: 'var(--space-2)'
            }}>
                <button
                    className="btn btn-secondary"
                    onClick={handleDiscard}
                    style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--space-2)'
                    }}
                >
                    <RotateCcw size={16} />
                    {isFormDirty ? 'Discard' : 'Close'}
                </button>
                <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={!isFormDirty}
                    style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--space-2)',
                        opacity: isFormDirty ? 1 : 0.5
                    }}
                >
                    <Save size={16} />
                    Save
                </button>
            </div>

            {/* Discard Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDiscardConfirm}
                onClose={() => setShowDiscardConfirm(false)}
                onConfirm={confirmDiscard}
                title="Discard Changes?"
                message="You have unsaved changes. Are you sure you want to discard them?"
                confirmText="Discard"
                cancelText="Keep Editing"
                variant="danger"
            />
        </ResizableContainer>
    );
};

export default PropertiesPanel;
